import {zodResolver} from '@hookform/resolvers/zod';
import {AnimatePresence, motion, useReducedMotion} from 'motion/react';
import {useRef, useState} from 'react';
import {useForm, type FieldPath} from 'react-hook-form';
import {toast} from 'sonner';

import {config} from '@/config';
import {trackRegistrationSuccess} from '@/lib/analytics';
import {asset} from '@/lib/asset';
import {newIdempotencyKey} from '@/lib/idempotency';
import {MUNICIPIOS} from '@/lib/municipios';
import {RateLimitError, ValidationFailure, submitRegistration} from '@/lib/registration-api';
import {API_FIELD_TO_FORM} from '@/lib/registration-payload';
import {
  AMPERAJES,
  BOOTH_ITEMS,
  CATEGORIAS,
  digitsOnly,
  PHONE_MAX_DIGITS,
  registrationSchema,
  type VendorRegistration,
} from '@/lib/registration-schema';
import {FORM_TITLE, isRegistrationOpen} from '@/lib/registration-window';
import {cn} from '@/lib/utils';

import {Reveal} from '@/components/motion/Reveal';
import {Button} from '@/components/ui/button';
import {Checkbox} from '@/components/ui/checkbox';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {RadioGroup, RadioGroupItem} from '@/components/ui/radio-group';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Textarea} from '@/components/ui/textarea';

const inputStyles =
  'h-11 rounded-lg border-white/12 bg-white/6 text-white placeholder:text-cream/45 focus-visible:border-amber/60 focus-visible:ring-amber/40 aria-invalid:border-destructive aria-invalid:ring-destructive/30';

function FieldError({id, message}: {id: string; message?: string}) {
  if (!message) return null;
  return (
    <motion.p
      id={id}
      role="alert"
      initial={{opacity: 0, y: -4}}
      animate={{opacity: 1, y: 0}}
      className="mt-1.5 text-sm text-destructive"
    >
      {message}
    </motion.p>
  );
}

function Group({legend, hint, children}: {legend: string; hint?: string; children: React.ReactNode}) {
  return (
    <fieldset className="border-t border-white/10 pt-8">
      <legend className="float-left mb-5 w-full">
        <span className="text-lg font-bold text-white">{legend}</span>
        {hint && <span className="mt-1 block text-sm text-cream/75">{hint}</span>}
      </legend>
      <div className="clear-both space-y-5">{children}</div>
    </fieldset>
  );
}

function SuccessScreen({businessName}: {businessName: string}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : {opacity: 0, scale: 0.96}}
      animate={{opacity: 1, scale: 1}}
      transition={{duration: 0.5, ease: [0.16, 1, 0.3, 1]}}
      className="mx-auto max-w-lg py-10 text-center"
      role="status"
    >
      <motion.svg
        viewBox="0 0 52 52"
        className="mx-auto h-20 w-20"
        initial={reduced ? undefined : 'hidden'}
        animate="visible"
        aria-hidden
      >
        <motion.circle
          cx="26"
          cy="26"
          r="24"
          fill="none"
          stroke="var(--amber)"
          strokeWidth="2.5"
          variants={{hidden: {pathLength: 0}, visible: {pathLength: 1}}}
          transition={{duration: 0.7, ease: 'easeOut'}}
        />
        <motion.path
          d="M15 27 l8 8 l15 -17"
          fill="none"
          stroke="var(--amber)"
          strokeWidth="3"
          strokeLinecap="round"
          variants={{hidden: {pathLength: 0}, visible: {pathLength: 1}}}
          transition={{duration: 0.45, delay: 0.55, ease: 'easeOut'}}
        />
      </motion.svg>
      <h3 className="mt-6 text-2xl font-bold text-white">¡Solicitud enviada!</h3>
      <p className="mt-3 text-pretty leading-relaxed text-cream/90">
        Gracias, <span className="font-medium text-amber">{businessName}</span>. Recibimos tu solicitud para Navimarket
        2026. Si tu negocio es seleccionado, te contactaremos por correo electrónico con los próximos pasos.
      </p>
      <img src={asset('img/elements/gift.webp')} alt="" className="mx-auto mt-8 w-32" loading="lazy" />
    </motion.div>
  );
}

/** La convocatoria cerró: se agradece a quien llegó tarde y se explica qué sigue. */
function ClosedScreen() {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : {opacity: 0, scale: 0.96}}
      animate={{opacity: 1, scale: 1}}
      transition={{duration: 0.5, ease: [0.16, 1, 0.3, 1]}}
      className="mx-auto max-w-lg py-10 text-center"
      role="status"
    >
      <img src={asset('img/logos/tree-cream.webp')} alt="" className="mx-auto w-16 opacity-90" />
      <h3 className="mt-6 text-2xl font-bold text-white">¡Gracias! La convocatoria ya cerró.</h3>
      <p className="mt-3 text-pretty leading-relaxed text-cream/90">{config.CLOSED_MESSAGE}</p>
      <img src={asset('img/elements/gift.webp')} alt="" className="mx-auto mt-8 w-32" loading="lazy" />
    </motion.div>
  );
}

export function VendorForm() {
  const reduced = useReducedMotion();
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [shake, setShake] = useState(0);
  const isOpen = isRegistrationOpen();

  const form = useForm<VendorRegistration>({
    resolver: zodResolver(registrationSchema),
    mode: 'onTouched',
    defaultValues: {
      businessName: '',
      contactName: '',
      role: '',
      email: '',
      phone: '',
      municipio: '',
      categoryOther: '',
      pathName: '',
      instagram: '',
      facebook: '',
      website: '',
      boothNeeds: [],
      products: '',
      merchantId: '',
      amperage: '',
    },
  });

  const {register, handleSubmit, watch, setValue, setError, formState} = form;
  // Se conserva entre reintentos: reusar la misma clave es lo que evita el
  // registro duplicado. Se limpia solo cuando el envío tiene éxito.
  const idempotencyKey = useRef<string | null>(null);
  const {errors, isSubmitting} = formState;

  // El teléfono se registra aparte porque su onChange se envuelve para
  // filtrar la entrada antes de que react-hook-form la reciba.
  const phoneField = register('phone');

  const category = watch('category');
  const hasAthBusiness = watch('hasAthBusiness');
  const isEvertec = watch('isEvertecMerchant');
  const boothNeeds = watch('boothNeeds');
  const needsElectricity = watch('needsElectricity');

  function toggleBooth(id: string, checked: boolean) {
    let next: string[];
    if (id === 'ninguno') {
      next = checked ? ['ninguno'] : [];
    } else {
      next = checked ? [...boothNeeds.filter((b) => b !== 'ninguno'), id] : boothNeeds.filter((b) => b !== id);
    }
    setValue('boothNeeds', next, {shouldValidate: formState.submitCount > 0});
  }

  async function onSubmit(data: VendorRegistration) {
    idempotencyKey.current ??= newIdempotencyKey();
    try {
      await submitRegistration(data, idempotencyKey.current);
      idempotencyKey.current = null;
      // Solo cuando la API confirma: el evento de GTM mide registros reales,
      // no intentos. El teléfono viaja igual que en el payload, solo dígitos.
      trackRegistrationSuccess({
        name: data.contactName,
        email: data.email,
        phone: digitsOnly(data.phone),
      });
      setSubmitted(data.businessName);
      // el form colapsa a la card de éxito; reanclar para que se vea
      requestAnimationFrame(() =>
        document.getElementById('registro')?.scrollIntoView({behavior: reduced ? 'auto' : 'smooth'}),
      );
    } catch (e) {
      // La clave se conserva: el reintento del usuario reusa la misma.
      if (e instanceof ValidationFailure && applyServerErrors(e.fields)) return;
      if (e instanceof RateLimitError) {
        toast.error('Demasiados intentos', {
          description: 'Espera unos segundos antes de volver a enviar tu solicitud.',
        });
        return;
      }
      toast.error('No pudimos enviar tu solicitud', {
        description: 'Algo falló de nuestro lado. Espera un momento e inténtalo otra vez.',
      });
    }
  }

  /**
   * Pinta los errores por campo que devuelve la API (400) bajo cada input.
   * Devuelve false si ninguno tiene equivalente en el formulario, para no
   * tragarse el error y caer al toast genérico.
   */
  function applyServerErrors(fields: {field: string; message: string}[]): boolean {
    const known = fields
      .map((f) => ({name: API_FIELD_TO_FORM[f.field], message: f.message}))
      .filter((f): f is {name: keyof VendorRegistration; message: string} => !!f.name);

    const first = known[0];
    if (!first) return false;
    for (const {name, message} of known) setError(name, {message});
    focusFirstInvalid(first.name);
    toast.error('Revisa los campos marcados', {
      description: 'Corrige lo señalado y vuelve a enviar tu solicitud.',
    });
    return true;
  }

  function onInvalid() {
    focusFirstInvalid(Object.keys(errors)[0] as FieldPath<VendorRegistration> | undefined);
  }

  function focusFirstInvalid(field: FieldPath<VendorRegistration> | undefined) {
    setShake((s) => s + 1);
    if (!field) return;
    document
      .querySelector(`[name="${field}"], [data-field="${field}"]`)
      ?.scrollIntoView({behavior: reduced ? 'auto' : 'smooth', block: 'center'});
  }

  const err = (name: keyof VendorRegistration) => errors[name]?.message;
  const aria = (name: keyof VendorRegistration) => ({
    'aria-invalid': !!errors[name] || undefined,
    'aria-describedby': errors[name] ? `err-${name}` : undefined,
  });

  return (
    <section id="registro" aria-labelledby="registro-title" className="relative scroll-mt-28 py-16 lg:py-20">
      {/* banda que ancla visualmente la sección más importante */}
      <div aria-hidden className="absolute inset-0 bg-pine-950/60" />
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6">
        <Reveal>
          <p className="overline-label text-amber">{isOpen ? 'Formulario de solicitud' : 'Convocatoria 2026'}</p>
          <h2 id="registro-title" className="mt-3 text-balance text-3xl font-bold text-white sm:text-4xl">
            {FORM_TITLE}
          </h2>
          {isOpen && (
            <p className="mt-3 max-w-xl text-pretty leading-relaxed text-cream/90">
              Convocatoria Navimarket 2026. Los campos marcados con{' '}
              <span aria-hidden className="text-amber">
                *
              </span>
              <span className="sr-only">asterisco</span> son obligatorios.
            </p>
          )}
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <div className="rounded-2xl border border-white/10 bg-pine-800 p-6 shadow-[0_24px_60px_-24px_rgba(0,20,16,0.8)] sm:p-10">
            {!isOpen ? (
              <ClosedScreen />
            ) : submitted ? (
              <SuccessScreen businessName={submitted} />
            ) : (
              <motion.form
                key={shake}
                animate={reduced || shake === 0 ? undefined : {x: [0, -8, 8, -5, 5, 0]}}
                transition={{duration: 0.4}}
                onSubmit={handleSubmit(onSubmit, onInvalid)}
                noValidate
                className="space-y-10"
              >
                {/* ── Información del negocio ─────────────────────── */}
                <fieldset>
                  <legend className="float-left mb-5 w-full text-lg font-bold text-white">
                    Información del negocio
                  </legend>
                  <div className="clear-both grid gap-5 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Label htmlFor="businessName" className="text-cream">
                        Nombre del negocio *
                      </Label>
                      <Input
                        id="businessName"
                        className={cn(inputStyles, 'mt-2')}
                        autoComplete="organization"
                        {...register('businessName')}
                        {...aria('businessName')}
                      />
                      <FieldError id="err-businessName" message={err('businessName')} />
                    </div>
                    <div>
                      <Label htmlFor="contactName" className="text-cream">
                        Contacto principal *
                      </Label>
                      <Input
                        id="contactName"
                        placeholder="Nombre y apellido"
                        className={cn(inputStyles, 'mt-2')}
                        autoComplete="name"
                        {...register('contactName')}
                        {...aria('contactName')}
                      />
                      <FieldError id="err-contactName" message={err('contactName')} />
                    </div>
                    <div>
                      <Label htmlFor="role" className="text-cream">
                        Cargo *
                      </Label>
                      <Input
                        id="role"
                        placeholder="Ej. Dueña, gerente…"
                        className={cn(inputStyles, 'mt-2')}
                        autoComplete="organization-title"
                        {...register('role')}
                        {...aria('role')}
                      />
                      <FieldError id="err-role" message={err('role')} />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-cream">
                        Correo electrónico *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        inputMode="email"
                        className={cn(inputStyles, 'mt-2')}
                        autoComplete="email"
                        {...register('email')}
                        {...aria('email')}
                      />
                      <FieldError id="err-email" message={err('email')} />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-cream">
                        Teléfono *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        inputMode="numeric"
                        placeholder="7875550123"
                        className={cn(inputStyles, 'mt-2')}
                        autoComplete="tel"
                        {...phoneField}
                        onChange={(e) => {
                          // Se limpia mientras se escribe: guiones, espacios y
                          // "+" (que en la hoja de cálculo del cliente abriría
                          // una fórmula) nunca llegan a entrar en el campo, ni
                          // escritos ni pegados. El tope se aplica aquí y no
                          // con maxLength, que recortaría el texto pegado antes
                          // de quitarle el formato y se comería dígitos buenos.
                          e.target.value = digitsOnly(e.target.value).slice(0, PHONE_MAX_DIGITS);
                          return phoneField.onChange(e);
                        }}
                        {...aria('phone')}
                      />
                      <FieldError id="err-phone" message={err('phone')} />
                      <p className="mt-1.5 text-xs text-cream/70">Solo números, sin guiones ni espacios.</p>
                    </div>
                    <div data-field="municipio">
                      <Label htmlFor="municipio-trigger" className="text-cream">
                        Municipio *
                      </Label>
                      <Select
                        value={watch('municipio')}
                        onValueChange={(v) => {
                          setValue('municipio', v, {shouldValidate: true});
                        }}
                      >
                        <SelectTrigger
                          id="municipio-trigger"
                          className={cn(inputStyles, 'mt-2 w-full')}
                          {...aria('municipio')}
                        >
                          <SelectValue placeholder="Selecciona…" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72 border-white/10 bg-pine-800 text-white">
                          {MUNICIPIOS.map((m) => (
                            <SelectItem key={m} value={m} className="focus:bg-white/10 focus:text-white">
                              {m}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FieldError id="err-municipio" message={err('municipio')} />
                    </div>
                    <div data-field="category">
                      <Label htmlFor="category-trigger" className="text-cream">
                        Categoría del negocio *
                      </Label>
                      <Select
                        value={category}
                        onValueChange={(v) => {
                          setValue('category', v as VendorRegistration['category'], {shouldValidate: true});
                        }}
                      >
                        <SelectTrigger
                          id="category-trigger"
                          className={cn(inputStyles, 'mt-2 w-full')}
                          {...aria('category')}
                        >
                          <SelectValue placeholder="Selecciona…" />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-pine-800 text-white">
                          {CATEGORIAS.map((c) => (
                            <SelectItem key={c} value={c} className="focus:bg-white/10 focus:text-white">
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FieldError id="err-category" message={err('category')} />
                    </div>
                    <AnimatePresence>
                      {category === 'Otro' && (
                        <motion.div
                          initial={reduced ? false : {opacity: 0, height: 0}}
                          animate={{opacity: 1, height: 'auto'}}
                          exit={{opacity: 0, height: 0}}
                          className="sm:col-span-2"
                        >
                          <Label htmlFor="categoryOther" className="text-cream">
                            ¿Cuál? *
                          </Label>
                          <Input
                            id="categoryOther"
                            className={cn(inputStyles, 'mt-2')}
                            {...register('categoryOther')}
                            {...aria('categoryOther')}
                          />
                          <FieldError id="err-categoryOther" message={err('categoryOther')} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </fieldset>

                {/* ── ATH Business ────────────────────────────────── */}
                <Group legend="ATH Business">
                  <div data-field="hasAthBusiness">
                    <Label className="text-cream">¿Tu negocio ya está registrado en ATH Business? *</Label>
                    <RadioGroup
                      className="mt-3 flex gap-6"
                      value={hasAthBusiness}
                      onValueChange={(v) => {
                        setValue('hasAthBusiness', v as 'si' | 'no', {shouldValidate: true});
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="si" id="ath-si" className="border-white/30 text-amber" />
                        <Label htmlFor="ath-si" className="font-normal text-white">
                          Sí
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="ath-no" className="border-white/30 text-amber" />
                        <Label htmlFor="ath-no" className="font-normal text-white">
                          No (quiero registrarme)
                        </Label>
                      </div>
                    </RadioGroup>
                    <FieldError id="err-hasAthBusiness" message={err('hasAthBusiness')} />
                  </div>

                  <AnimatePresence>
                    {hasAthBusiness === 'no' && (
                      <motion.div
                        initial={reduced ? false : {opacity: 0, height: 0}}
                        animate={{opacity: 1, height: 'auto'}}
                        exit={{opacity: 0, height: 0}}
                      >
                        <p
                          className="rounded-lg border border-amber/30 bg-amber/10 px-4 py-3 text-sm leading-relaxed text-cream"
                          role="note"
                        >
                          Para participar en Navimarket deberás completar tu registro de{' '}
                          <a
                            href="https://ath.business/nuevo-registro"
                            target="_blank"
                            rel="noreferrer"
                            className="font-medium text-amber underline underline-offset-4"
                          >
                            ATH Business
                          </a>{' '}
                          antes del evento. Puedes enviar tu solicitud ahora y registrarte en lo que llega la
                          confirmación.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div>
                    <Label htmlFor="pathName" className="text-cream">
                      Nombre de tu negocio en ATH Business (/pATH){' '}
                      {hasAthBusiness === 'no' ? <span className="text-cream/60">(cuando lo tengas)</span> : '*'}
                    </Label>
                    <Input
                      id="pathName"
                      placeholder="/tunegocio"
                      className={cn(inputStyles, 'mt-2')}
                      {...register('pathName')}
                      {...aria('pathName')}
                    />
                    <FieldError id="err-pathName" message={err('pathName')} />
                  </div>
                </Group>

                {/* ── Participación ───────────────────────────────── */}
                <Group legend="Participación">
                  <div data-field="participatedBefore">
                    <Label className="text-cream">¿Has participado anteriormente en NaviMarket? *</Label>
                    <RadioGroup
                      className="mt-3 flex gap-6"
                      value={watch('participatedBefore')}
                      onValueChange={(v) => {
                        setValue('participatedBefore', v as 'si' | 'no', {shouldValidate: true});
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="si" id="prev-si" className="border-white/30 text-amber" />
                        <Label htmlFor="prev-si" className="font-normal text-white">
                          Sí
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="prev-no" className="border-white/30 text-amber" />
                        <Label htmlFor="prev-no" className="font-normal text-white">
                          No
                        </Label>
                      </div>
                    </RadioGroup>
                    <FieldError id="err-participatedBefore" message={err('participatedBefore')} />
                  </div>
                </Group>

                {/* ── Redes sociales ──────────────────────────────── */}
                <Group legend="Redes sociales">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="instagram" className="text-cream">
                        Instagram *
                      </Label>
                      <Input
                        id="instagram"
                        placeholder="@tunegocio"
                        className={cn(inputStyles, 'mt-2')}
                        {...register('instagram')}
                        {...aria('instagram')}
                      />
                      <FieldError id="err-instagram" message={err('instagram')} />
                    </div>
                    <div>
                      <Label htmlFor="facebook" className="text-cream">
                        Facebook <span className="text-cream/60">(opcional)</span>
                      </Label>
                      <Input id="facebook" className={cn(inputStyles, 'mt-2')} {...register('facebook')} />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="website" className="text-cream">
                        Página web <span className="text-cream/60">(opcional)</span>
                      </Label>
                      <Input
                        id="website"
                        type="url"
                        inputMode="url"
                        placeholder="https://…"
                        className={cn(inputStyles, 'mt-2')}
                        {...register('website')}
                      />
                    </div>
                  </div>
                </Group>

                {/* ── Información del espacio ─────────────────────── */}
                <Group
                  legend="Información del espacio"
                  hint="¿Necesitarás que te proveamos alguno de los siguientes en tu booth de 8×8? Selecciona todo lo que aplique."
                >
                  <div data-field="boothNeeds" className="grid gap-3 sm:grid-cols-2">
                    {BOOTH_ITEMS.map((item) => {
                      const checked = boothNeeds.includes(item.id);
                      return (
                        <label
                          key={item.id}
                          className={cn(
                            'flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition-colors',
                            checked
                              ? 'border-amber/60 bg-amber/10'
                              : 'border-white/12 bg-white/4 hover:border-white/25',
                          )}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(c) => {
                              toggleBooth(item.id, c === true);
                            }}
                            className="border-white/30 data-[state=checked]:border-amber data-[state=checked]:bg-amber data-[state=checked]:text-ink"
                          />
                          <span className="text-sm text-white">
                            {item.label}
                            {'hint' in item && item.hint && (
                              <span className="block text-xs text-cream/70">{item.hint}</span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <FieldError id="err-boothNeeds" message={err('boothNeeds')} />

                  <div data-field="needsElectricity" className="border-t border-white/10 pt-5">
                    <Label className="text-cream">¿Necesitarás electricidad en tu booth? *</Label>
                    <RadioGroup
                      className="mt-3 flex gap-6"
                      value={needsElectricity}
                      onValueChange={(v) => {
                        setValue('needsElectricity', v as 'si' | 'no', {shouldValidate: true});
                        // Al volver a "no" se descarta el amperaje: si no se
                        // limpiara, un valor viejo quedaría en el formulario y
                        // el error de "selecciona el amperaje" podría seguir
                        // pintado bajo un campo que ya no se ve.
                        if (v === 'no') setValue('amperage', '', {shouldValidate: true});
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="si" id="elec-si" className="border-white/30 text-amber" />
                        <Label htmlFor="elec-si" className="font-normal text-white">
                          Sí
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="elec-no" className="border-white/30 text-amber" />
                        <Label htmlFor="elec-no" className="font-normal text-white">
                          No
                        </Label>
                      </div>
                    </RadioGroup>
                    <FieldError id="err-needsElectricity" message={err('needsElectricity')} />
                  </div>

                  <AnimatePresence>
                    {needsElectricity === 'si' && (
                      <motion.div
                        initial={reduced ? false : {opacity: 0, height: 0}}
                        animate={{opacity: 1, height: 'auto'}}
                        exit={{opacity: 0, height: 0}}
                      >
                        <div data-field="amperage">
                          <Label htmlFor="amperage-trigger" className="text-cream">
                            ¿Cuánto amperaje necesitas? *
                          </Label>
                          <Select
                            value={watch('amperage')}
                            onValueChange={(v) => {
                              setValue('amperage', v, {shouldValidate: true});
                            }}
                          >
                            <SelectTrigger
                              id="amperage-trigger"
                              className={cn(inputStyles, 'mt-2 w-full sm:max-w-xs')}
                              {...aria('amperage')}
                            >
                              <SelectValue placeholder="Selecciona…" />
                            </SelectTrigger>
                            <SelectContent className="border-white/10 bg-pine-800 text-white">
                              {AMPERAJES.map((a) => (
                                <SelectItem
                                  key={a.value}
                                  value={a.value}
                                  className="focus:bg-white/10 focus:text-white"
                                >
                                  {a.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FieldError id="err-amperage" message={err('amperage')} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Group>

                {/* ── Productos ───────────────────────────────────── */}
                <Group legend="Productos">
                  <div>
                    <Label htmlFor="products" className="text-cream">
                      ¿Qué productos o servicios venderás durante el evento? *
                    </Label>
                    <Textarea
                      id="products"
                      rows={4}
                      placeholder="Cuéntanos qué llevas al bazar…"
                      className={cn(inputStyles, 'mt-2 h-auto min-h-28')}
                      {...register('products')}
                      {...aria('products')}
                    />
                    <FieldError id="err-products" message={err('products')} />
                  </div>
                </Group>

                {/* ── Servicios de Evertec ────────────────────────── */}
                <Group legend="Servicios de Evertec">
                  <div data-field="isEvertecMerchant">
                    <Label className="text-cream">¿Qué otro servicio de Evertec tienes? *</Label>
                    <RadioGroup
                      className="mt-3 flex flex-col gap-3"
                      value={isEvertec}
                      onValueChange={(v) => {
                        setValue('isEvertecMerchant', v as 'pos' | 'otro' | 'ninguno', {shouldValidate: true});
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="pos" id="ev-pos" className="border-white/30 text-amber" />
                        <Label htmlFor="ev-pos" className="font-normal text-white">
                          POS de Evertec
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="otro" id="ev-otro" className="border-white/30 text-amber" />
                        <Label htmlFor="ev-otro" className="font-normal text-white">
                          Otro
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="ninguno" id="ev-ninguno" className="border-white/30 text-amber" />
                        <Label htmlFor="ev-ninguno" className="font-normal text-white">
                          No cuento con otro servicio de Evertec
                        </Label>
                      </div>
                    </RadioGroup>
                    <FieldError id="err-isEvertecMerchant" message={err('isEvertecMerchant')} />
                  </div>

                  <AnimatePresence>
                    {isEvertec === 'otro' && (
                      <motion.div
                        initial={reduced ? false : {opacity: 0, height: 0}}
                        animate={{opacity: 1, height: 'auto'}}
                        exit={{opacity: 0, height: 0}}
                      >
                        <Label htmlFor="merchantId" className="text-cream">
                          ¿Cuál? *
                        </Label>
                        <Input
                          id="merchantId"
                          className={cn(inputStyles, 'mt-2')}
                          {...register('merchantId')}
                          {...aria('merchantId')}
                        />
                        <FieldError id="err-merchantId" message={err('merchantId')} />
                        <p className="mt-1.5 text-xs text-cream/70">
                          Escribe el nombre del servicio de Evertec que utilizas.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Group>

                {/* ── Confirmación + submit ───────────────────────── */}
                <div className="border-t border-white/10 pt-8">
                  <label data-field="confirm" className="flex cursor-pointer items-start gap-3">
                    <Checkbox
                      checked={watch('confirm')}
                      onCheckedChange={(c) => {
                        setValue('confirm', (c === true) as true, {shouldValidate: formState.submitCount > 0});
                      }}
                      className="mt-0.5 border-white/30 data-[state=checked]:border-amber data-[state=checked]:bg-amber data-[state=checked]:text-ink"
                      aria-describedby={errors.confirm ? 'err-confirm' : undefined}
                    />
                    <span className="text-sm leading-relaxed text-cream">
                      Confirmo que, de ser seleccionado, cumpliré con los requisitos y fechas establecidas para
                      participar en Navimarket 2026. *
                    </span>
                  </label>
                  <FieldError id="err-confirm" message={errors.confirm?.message} />

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="mt-8 h-13 w-full rounded-full bg-tangerine text-base font-medium text-white shadow-[0_8px_30px_-6px_rgba(222,118,57,0.55)] transition-all hover:-translate-y-0.5 hover:bg-tangerine-deep disabled:opacity-70 sm:h-12"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2.5">
                        <motion.span
                          aria-hidden
                          className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white"
                          animate={reduced ? undefined : {rotate: 360}}
                          transition={{duration: 0.8, repeat: Infinity, ease: 'linear'}}
                        />
                        Enviando solicitud…
                      </span>
                    ) : (
                      'Enviar solicitud'
                    )}
                  </Button>
                  <p className="mt-4 text-center text-xs leading-relaxed text-cream/70">
                    Enviar la solicitud no garantiza un espacio. Te contactaremos por correo si tu negocio es
                    seleccionado.
                  </p>
                </div>
              </motion.form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
